import { addBreadcrumb, captureException } from "@/lib/client/sentryClient";

// Custom error classes for better error handling and tracking
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public endpoint?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
    public value?: unknown
  ) {
    super(message);
    this.name = "ValidationError";
  }
}

export class AuthenticationError extends Error {
  constructor(message: string = "Authentication failed") {
    super(message);
    this.name = "AuthenticationError";
  }
}

export class NetworkError extends Error {
  constructor(
    message: string = "Network request failed",
    public url?: string
  ) {
    super(message);
    this.name = "NetworkError";
  }
}

export class RateLimitError extends Error {
  constructor(
    message: string = "Rate limit exceeded",
    public retryAfter?: number
  ) {
    super(message);
    this.name = "RateLimitError";
  }
}

// User-friendly error messages mapping
const ERROR_MESSAGES: Record<string, string> = {
  ApiError: "Unable to connect to the service. Please try again.",
  ValidationError: "Please check your input and try again.",
  AuthenticationError: "Please sign in to continue.",
  NetworkError: "Network connection lost. Please check your internet.",
  RateLimitError: "Too many requests. Please wait a moment.",
  default: "Something went wrong. Please try again.",
};

export function getUserMessage(error: Error): string {
  if (error.name in ERROR_MESSAGES) {
    return ERROR_MESSAGES[error.name];
  }
  return ERROR_MESSAGES.default;
}

const CLERK_MIDDLEWARE_DETECTION_MESSAGE =
  "Clerk: auth() was called but Clerk can't detect usage of clerkMiddleware()";

export function isClerkMiddlewareDetectionError(error: Error | unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String(error ?? "");

  return message.includes(CLERK_MIDDLEWARE_DETECTION_MESSAGE);
}

// Enhanced error logging to Sentry with context
export interface ErrorContext {
  [key: string]: unknown;
}

export function logError(error: Error | unknown, context?: ErrorContext): void {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.error("[Error]", errorObj, context);
  }

  // Send to Sentry with context
  captureException(errorObj, {
    extra: context,
    tags: {
      errorType: errorObj.name,
    },
  });
}

export function logWarning(message: string, context?: ErrorContext): void {
  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.warn("[Warning]", message, context);
  }

  // Send to Sentry as a warning/breadcrumb
  addBreadcrumb({
    category: "warning",
    message,
    level: "warning",
    data: context,
  });
}

export function logInfo(message: string, context?: ErrorContext): void {
  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    console.log("[Info]", message, context);
  }

  // Add breadcrumb for context
  addBreadcrumb({
    category: "info",
    message,
    level: "info",
    data: context,
  });
}

// Specific logging functions for common scenarios
export function logApiError(error: unknown, endpoint: string, context?: ErrorContext): void {
  const apiError =
    error instanceof ApiError
      ? error
      : new ApiError(
          error instanceof Error ? error.message : String(error),
          undefined,
          endpoint,
          context
        );
  const errorContext: ErrorContext = {
    endpoint: apiError.endpoint ?? endpoint,
    ...context,
  };

  logError(apiError, errorContext);
}

export function logValidationError(field: string, value: unknown, message?: string): void {
  logError(new ValidationError(message || `Validation failed for ${field}`, field, value), {
    field,
    value,
  });
}

// React Error Boundary error callback
export function logErrorBoundary(error: Error, errorInfo: React.ErrorInfo): void {
  logError(error, {
    componentStack: errorInfo.componentStack,
    errorBoundary: true,
  });
}

// Async error handler for promises
export function asyncErrorHandler<T>(promise: Promise<T>): Promise<[T | null, Error | null]> {
  return promise
    .then<[T, null]>((data) => [data, null])
    .catch<[null, Error]>((error) => {
      logError(error);
      return [null, error instanceof Error ? error : new Error(String(error))];
    });
}

// Safe function wrapper that catches and logs errors
export function safeAsync<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context?: ErrorContext
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      logError(error, context);
      return null;
    }
  }) as T;
}
